import { PlanStack } from '@zapier/design-system-revenue/src/types';
import { BillingInterval } from '@zapier/design-system-revenue/src/graphql-types';
import { planIds } from './planIds';

const commonProps = {
  __typename: 'PlanV2' as 'PlanV2',
  allowCompanies: true,
  hasAutoReplay: true,
  hasPaths: true,
  hasMultiStepZaps: true,
  hasPremiumApps: true,
  hasTeams: true,
  isInPlanGroup: true,
  hasMeteredBilling: false,
  tiers: [],
  description: 'Bring your team together to collaborate on automation.',
  features: [
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Multi-step Zaps',
      tooltip:
        'Multi-Step Zaps let a single Trigger (e.g. new email lead) perform as many Actions (e.g. add to my CRM) as you want.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: '/apps?filter=premium',
      name: 'Unlimited Premium Apps',
      tooltip:
        'Premium apps are select apps that are exclusively available to users on a paid plans.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Filters',
      tooltip:
        'Filters tell your Zaps to run only if your data matches certain criteria.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Formatters',
      tooltip:
        'Formatters let you tweak numbers, dates, and text in over a dozen ways to get it in the format you need.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Custom Integrations',
      tooltip:
        'Build powerful, custom integrations that connect any app with Webhooks by Zapier.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Custom Logic - Paths',
      tooltip:
        'Build advanced workflows using branching logic to run different actions based on conditions you decide.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Auto Replay',
      tooltip:
        'Intelligently retries any Task failures due to temporary errors or downtime for you.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Unlimited Users',
      tooltip: 'Add unlimited users to your account at no additional cost.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Folder Permissions',
      tooltip: 'Control who can edit shared Zaps and access shared folders.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Premier Support',
      tooltip:
        'Get faster, prioritized responses from our dedicated Premier Support team.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Shared App Connections',
      tooltip:
        'Connect your team’s favorite tools, like Trello, Dropbox, or Typeform, so everyone can use them in their workflows without needing to share passwords and API keys.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Shared Workspace',
      tooltip: 'Share access to your Zaps with your team.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Unlimited Workspaces',
      tooltip:
        'Add unlimited teams to your account for managing workspace-level permissions.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'User provisioning (SCIM)',
      tooltip:
        'Automatically create, change, disable, and delete user accounts to ensure the right people have access.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Apps Access Control',
      tooltip:
        'Manage which apps and services employees can connect to your Zapier account.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'SAML Single Sign On (SSO)',
      tooltip:
        'Ensure secure access for your team using SAML 2.0 identity and access management (IAM) providers.',
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Custom Data Retention',
      tooltip:
        "Customize your Task history retention to match your company's legal and regulatory requirements.",
    },
    {
      __typename: 'PlanFeature' as 'PlanFeature',
      href: null,
      name: 'Account Capture',
      tooltip:
        'Make sure your entire company is using a single account and that your security policies are enforced company wide.',
    },
  ],
  type: 'Company' as 'Company',
  zaps: -1,
  zapsRunInterval: 1,
};

export const companyPlanStackMock: PlanStack = {
  __typename: 'PlanTaskGrouping',
  id: 'Company',
  plans: [
    {
      ...commonProps,
      amount: 718800,
      interval: BillingInterval.year,
      id: planIds.COMPANY_100000_YEARLY,
      name: 'Company (100,000 Tasks)',
      tasks: 100000,
    },
    {
      ...commonProps,
      amount: 74875,
      interval: BillingInterval.month,
      id: planIds.COMPANY_100000_MONTHLY,
      name: 'Company (100,000 Tasks)',
      tasks: 100000,
    },
    {
      ...commonProps,
      amount: 1078800,
      interval: BillingInterval.year,
      id: planIds.COMPANY_200000_YEARLY,
      name: 'Company (200,000 Tasks)',
      tasks: 200000,
    },
    {
      ...commonProps,
      amount: 112375,
      interval: BillingInterval.month,
      id: planIds.COMPANY_200000_MONTHLY,
      name: 'Company (200,000 Tasks)',
      tasks: 200000,
    },
  ],
  planType: 'Company',
};
